/**
 * @file PreviewSection.tsx
 * @module PreviewSection
 * @description
 *   Renders a preview section for campaign assets, allowing users to
 *   toggle between selected, unselected, and video preview states.
 *   Handles loading and displaying assets from presigned URLs and provides
 *   fallback placeholders when assets are missing.
 */

import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { KatButton, KatAlert } from '@amzn/katal-react';
import { PreviewAssets, PreviewSectionState, PreviewState } from 'src/types';
import { generatePreview } from 'src/utils/preview/PreviewGenerator';
import styles from './PreviewSection.module.css';
import {
  ErrorConstants,
  PLACEHOLDER_URLS,
  PREVIEW_STATES,
  PreviewConstants,
  StateConstants,
} from 'src/constants';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { logger } from 'src/logger';
import { AssetContextState } from 'src/contexts/AssetContext';
import {
  getChangedAssetTypes,
  areAnyAssetsInLoadingStates,
  getAssetUrls,
} from 'src/utils/preview/previewUtils';
import { LoadingOverlay } from 'src/components/general/LoadingOverlay';

/**
 * Props for the PreviewControls component
 * @interface PreviewControlsProps
 * @property {PreviewState} activeState - Currently active preview state
 * @property {(state: PreviewState) => void} onStateChange - Handler for state changes
 * @property {readonly PreviewState[]} availableStates - Array of available preview states
 */
interface PreviewControlsProps {
  activeState: PreviewState;
  onStateChange: (state: PreviewState) => void;
  availableStates: readonly PreviewState[];
}

/**
 * Props for the PreviewContent component
 * @interface PreviewContentProps
 * @property {PreviewState} activeState - Currently active preview state
 * @property {string} url - URL of the preview content
 * @property {boolean} hasVideo - Whether video content is available
 */
interface PreviewContentProps {
  activeState: PreviewState;
  url: string;
  hasVideo: boolean;
}

/**
 * Renders preview state control buttons
 * @component PreviewControls
 * @param {PreviewControlsProps} props - Component props
 * @returns {JSX.Element} Preview controls UI
 */
const PreviewControls = memo(
  ({ activeState, onStateChange, availableStates }: PreviewControlsProps) => (
    <div className={styles.controls}>
      {availableStates.map((state) => (
        <KatButton
          key={state}
          variant={activeState === state ? 'primary' : 'secondary'}
          onClick={() => onStateChange(state)}
          data-testid={`preview-button-${state.toLowerCase()}`}
        >
          {state}
        </KatButton>
      ))}
    </div>
  ),
);

/**
 * Renders preview content based on active state
 * @component PreviewContent
 * @param {PreviewContentProps} props - Component props
 * @returns {JSX.Element} Preview content UI
 */
const PreviewContent = memo(({ activeState, url, hasVideo }: PreviewContentProps) => {
  if (activeState === PreviewConstants.PREVIEW_LABELS.VIDEO) {
    return hasVideo ? (
      <video src={url} controls className={styles.preview} data-testid="preview-video" />
    ) : (
      <img
        src={url}
        alt={PreviewConstants.PREVIEW_ALT_TEXT[activeState]}
        className={styles.preview}
        data-testid={`preview-placeholder-image-${activeState.toLowerCase()}`}
      />
    );
  }
  return (
    <img
      src={url}
      alt={PreviewConstants.PREVIEW_ALT_TEXT[activeState]}
      className={styles.preview}
      data-testid={`preview-image-${activeState.toLowerCase()}`}
    />
  );
});

/**
 * Main preview section component
 * Manages preview states and asset loading
 * @component PreviewSection
 * @returns {JSX.Element} Complete preview section UI
 */
const PreviewSection = memo(() => {
  // --- State ---
  const [previewSectionState, setPreviewSectionState] = useState<PreviewSectionState>({
    urls: {
      [PreviewConstants.PREVIEW_LABELS.SELECTED]: StateConstants.EMPTY_STRING,
      [PreviewConstants.PREVIEW_LABELS.UNSELECTED]: StateConstants.EMPTY_STRING,
      [PreviewConstants.PREVIEW_LABELS.VIDEO]: PLACEHOLDER_URLS[AssetType.VIDEO_TRAILER],
    },
    isLoading: false,
    activeState: PreviewConstants.PREVIEW_LABELS.SELECTED as PreviewState,
  });

  const updatePreviewSectionState = useCallback((updates: Partial<PreviewSectionState>) => {
    setPreviewSectionState((prev) => ({ ...prev, ...updates }));
  }, []);

  const { assetStates } = useAssetContext();

  const assetStateRef = useRef<AssetContextState>({} as AssetContextState);

  const controlPlaneClient = getControlPlaneClient();

  const assetUrlsRef = useRef<PreviewAssets>({
    [AssetType.BACKGROUND_IMAGE]: PLACEHOLDER_URLS[AssetType.BACKGROUND_IMAGE],
    [AssetType.LOGO]: PLACEHOLDER_URLS[AssetType.LOGO],
    [AssetType.VIDEO_TRAILER]: PLACEHOLDER_URLS[AssetType.VIDEO_TRAILER],
  });

  /**
   * Generates preview URLs using current asset URLs
   * @returns Promise resolving to an object with preview URLs
   */
  const generatePreviews = useCallback(async () => {
    const [selectedPreviewUrl, unselectedPreviewUrl] = await Promise.all([
      generatePreview(
        {
          backgroundImage: assetUrlsRef.current[AssetType.BACKGROUND_IMAGE],
          logo: assetUrlsRef.current[AssetType.LOGO],
        },
        PreviewConstants.PREVIEW_LABELS.SELECTED,
      ),
      generatePreview(
        {
          backgroundImage: assetUrlsRef.current[AssetType.BACKGROUND_IMAGE],
          logo: assetUrlsRef.current[AssetType.LOGO],
        },
        PreviewConstants.PREVIEW_LABELS.UNSELECTED,
      ),
    ]);

    return {
      [PreviewConstants.PREVIEW_LABELS.SELECTED]: selectedPreviewUrl,
      [PreviewConstants.PREVIEW_LABELS.UNSELECTED]: unselectedPreviewUrl,
      [PreviewConstants.PREVIEW_LABELS.VIDEO]:
        assetUrlsRef.current[AssetType.VIDEO_TRAILER] || PLACEHOLDER_URLS[AssetType.VIDEO_TRAILER],
    };
  }, []);

  const isMounted = useRef(false);

  // --- Effects ---
  /** Effect to fetch the latest asset urls and generate previews */
  useEffect(() => {
    isMounted.current = true;
    /** Step 1: check if any asset is in loading state,
     *  If yes, put the preview section in loading state and return
     *  Else, continue */
    if (areAnyAssetsInLoadingStates(assetStates)) {
      updatePreviewSectionState({
        isLoading: true,
      });
      return;
    }
    logger.debug('Asset states : ', { assetStates });
    /** If all assets are ready - proceed to get asset url's and generate preview */
    /** Step 2: Get the list of assets where the keys have changed*/
    const changedAssetTypes = getChangedAssetTypes(assetStates, assetStateRef.current);

    /** Step 3.1: If assets are changed*/
    if (changedAssetTypes.length > 0) {
      /** If any assets are changed - new preview need to be generated with new asset urls
       *  So we can safely put preview section to loading.
       */
      updatePreviewSectionState({ isLoading: true });

      /** Method to update the asset urls and generate previews */
      const updateAssetsAndGeneratePreviews = async () => {
        try {
          const changedAssets = changedAssetTypes.map((type) => {
            const asset = assetStates[type];
            return { type, key: asset.key };
          });
          if (isMounted.current) {
            /** Step 4: Get the asset urls for the changed assets */
            const assetToAssetUrlsMap = await getAssetUrls(changedAssets, controlPlaneClient);
            /** Step 5: Update the asset refs*/
            Object.entries(assetToAssetUrlsMap).forEach(([type, assetUrl]) => {
              assetUrlsRef.current[type as AssetType] = assetUrl;
            });
            /** Step 6: Generate the previews using latest asset refs */
            const previewUrls = await generatePreviews();
            /** Step 7: Update the preview urls for the respective states and set the loading to false */
            updatePreviewSectionState({
              urls: previewUrls,
              isLoading: false,
            });
          }
        } catch (err) {
          if (!isMounted.current) return;
          logger.error(ErrorConstants.MESSAGES.ERROR_UPDATING_PRESIGNED_URLS, { error: err });
          updatePreviewSectionState({
            error: ErrorConstants.MESSAGES.FAILED_TO_UPDATE_PREVIEW_ASSETS,
            isLoading: false,
          });
        }
      };

      updateAssetsAndGeneratePreviews();
    } else {
      /**Step 3.2: If assets are not changed */
      /** This could mean that the assets are in failed terminal states - put the preview loading to false*/
      updatePreviewSectionState({ isLoading: false });
    }

    assetStateRef.current = assetStates;

    return () => {
      isMounted.current = false;
    };
  }, [assetStates, controlPlaneClient, updatePreviewSectionState]);

  /** Effect on mount to initialize the preview with placeholder values */
  useEffect(() => {
    const initializePreviews = async () => {
      updatePreviewSectionState({ isLoading: true });
      try {
        const previewUrls = await generatePreviews();
        updatePreviewSectionState({
          urls: previewUrls,
          isLoading: false,
        });
      } catch (err) {
        updatePreviewSectionState({
          error: ErrorConstants.MESSAGES.FAILED_TO_UPDATE_PREVIEW_ASSETS,
          isLoading: false,
        });
      }
    };
    initializePreviews();
  }, []);

  // --- Handlers ---
  const handleStateChange = useCallback(
    (state: PreviewState) => {
      updatePreviewSectionState({
        activeState: state,
      });
    },
    [updatePreviewSectionState],
  );

  const hasVideoAsset = !!assetStates[AssetType.VIDEO_TRAILER]?.key;

  // --- Render ---
  return (
    <section className={styles.container}>
      <div className={styles.content}>
        <PreviewControls
          activeState={previewSectionState.activeState}
          onStateChange={handleStateChange}
          availableStates={PREVIEW_STATES}
        />

        {previewSectionState.isLoading ? (
          <LoadingOverlay text={PreviewConstants.LOADING_PREVIEW_TEXT} fullScreen={false} />
        ) : (
          <>
            {previewSectionState.error && (
              <KatAlert
                variant="danger"
                description={previewSectionState.error}
                data-testid="preview-error-alert"
              />
            )}

            <div className={styles.previewWrapper}>
              <PreviewContent
                activeState={previewSectionState.activeState}
                url={previewSectionState.urls[previewSectionState.activeState]}
                hasVideo={hasVideoAsset}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
});

PreviewSection.displayName = PreviewConstants.COMPONENT_NAMES.PREVIEW_SECTION;
export { PreviewSection };
